import { doc, onSnapshot, Timestamp } from 'firebase/firestore';

import { playNewMessageNotificationInWidget } from 'widget/helpers/utils';
import { CONVERSATION_STATUS } from 'widget/constants/messages';
import { startFirebase, stopFirebase } from 'widget/helpers/firebase';

import { ON_AGENT_MESSAGE_RECEIVED } from '../constants/widgetBusEvents';

function createFirestoreDocumentId(tenantId, widgetId, sessionId, firestoreDocumentIdSuffix) {
  return `${tenantId}_${widgetId}_${sessionId}${firestoreDocumentIdSuffix ? `_${firestoreDocumentIdSuffix}` : ''}`;
}

class SseEventsConnection {
  constructor(app, tenantId, widgetId, sessionId, firestoreDocumentIdSuffix) {
    this.firestoreDocumentId = createFirestoreDocumentId(tenantId, widgetId, sessionId, firestoreDocumentIdSuffix);

    this.connect();

    this.app = app;
    this.events = {};
    this.isAValidEvent = () => true;

    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.status_changed': this.onStatusChange,
      'conversation.created': this.onConversationCreated,
      'presence.update': this.onPresenceUpdate,
    };
  }

  async connect() {
    const firebase = await startFirebase();
    const startTimestamp = Timestamp.now();

    this.unsubscribe = onSnapshot(
      doc(firebase.firestore, `widget-events/${this.firestoreDocumentId}`),
      {
        next: (snapshot) => {
          const data = snapshot.data();

          if (data && data.timestamp && data.timestamp.toMillis() > startTimestamp.toMillis()) {
            this.onReceived({ event: data.event, data: data.data });
          }
        },
        error: (err) => {
          console.error('Error listening for events', err);
        }
      }
    );
  }

  async disconnect() {
    this.firestoreDocumentId = undefined;
    this.unsubscribe();

    await stopFirebase();
  }

  // eslint-disable-next-line class-methods-use-this
  onDisconnected() {
    window.bus.$emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };

  async reconnect(tenantId, widgetId, sessionId, firestoreDocumentIdSuffix) {
    await this.disconnect();
    this.firestoreDocumentId = createFirestoreDocumentId(tenantId, widgetId, sessionId, firestoreDocumentIdSuffix);
    await this.connect();
  }

  onStatusChange = data => {
    if (data.status === CONVERSATION_STATUS.CLOSED) {
      this.app.$store.dispatch('campaign/resetCampaign');
    }

    this.app.$store.dispatch('conversationAttributes/update', data);
  };

  onMessageCreated = data => {
    this.app.$store
      .dispatch('conversation/fetchMessageById', data)
      .then(message => {
        if (message.sender.type === 'user') {
          playNewMessageNotificationInWidget();
        }

        window.bus.$emit(ON_AGENT_MESSAGE_RECEIVED);
      });
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('conversation/addOrUpdateMessage', data);
  };

  onConversationCreated = () => {
    this.app.$store.dispatch('conversationAttributes/getAttributes');
  };

  onPresenceUpdate = data => {
    this.app.$store.dispatch('agent/updatePresence', data.users);
  };

  onTypingOn = data => {
    if (data.is_private) {
      return;
    }

    this.clearTimer();
    this.app.$store.dispatch('conversation/toggleAgentTyping', {
      status: 'on',
    });
    this.initTimer();
  };

  onTypingOff = () => {
    this.clearTimer();
    this.app.$store.dispatch('conversation/toggleAgentTyping', {
      status: 'off',
    });
  };

  clearTimer = () => {
    if (this.CancelTyping) {
      clearTimeout(this.CancelTyping);
      this.CancelTyping = null;
    }
  };

  initTimer = () => {
    // Turn off typing automatically after 30 seconds
    this.CancelTyping = setTimeout(() => {
      this.onTypingOff();
    }, 30000);
  };
}

export default SseEventsConnection;
